import React, {
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { Formik, Form, FieldArray, getIn } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { selectUser } from "../../redux/modules/userSlice";
import { MedicalAPI, UserAPI } from "../api";
import { ruleSchema, notifyFormInvalid } from "../../utils/inputRules";
import useDraft from "../../hooks/useDraft";
import {
  applySectionDraftData,
  pickSectionDraftData,
} from "../../drafts/draftHelpers";
import {
  JOINEE_FORM_COLORS,
  JOINEE_FORM_FONT,
  INSURANCE_RELATIONSHIP_OPTIONS,
  emptyInsuranceMember,
  getInsurancePolicyYearLabel,
} from "../../config/joineeFormConstants";
import {
  FormSectionTitle,
  FormAddButton,
  FormTextField,
  FormSelectField,
  FormDateField,
  FormTwoCol,
  FormCheckboxField,
} from "./JoineeFormFields";

var DRAFT_SECTION = "E";
var NAME_REGEX = /^[a-zA-Z\s.'-]+$/;
var ADDABLE_RELATIONSHIPS = INSURANCE_RELATIONSHIP_OPTIONS.filter(function (
  opt
) {
  return opt.value && opt.value !== "Self";
});

function errMsg(formik, path) {
  var touched = getIn(formik.touched, path);
  var error = getIn(formik.errors, path);
  return touched && error ? error : "";
}

function toTitleCaseName(value) {
  return String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .split(" ")
    .map(function (word) {
      if (!word) {
        return "";
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

function hydrateSelfRow(rows, userData, userId) {
  var list =
    rows && rows.length
      ? rows.slice()
      : [emptyInsuranceMember(userId, "Self")];
  var first = Object.assign({}, emptyInsuranceMember(userId, "Self"), list[0], {
    userId: userId,
    relationship: "Self",
  });
  if (userData) {
    if (userData.name) {
      first.name = userData.name;
    }
    if (userData.dateOfBirth && !first.dob) {
      first.dob = userData.dateOfBirth;
    } else if (userData.dateOfBirth && first.relationship === "Self") {
      first.dob = userData.dateOfBirth;
    }
  }
  list[0] = first;
  return list.map(function (row, index) {
    return Object.assign({}, emptyInsuranceMember(userId), row, {
      userId: userId,
      relationship:
        index === 0 ? "Self" : row.relationship ? row.relationship : "",
    });
  });
}

function relationshipOptionsForRow(members, index) {
  var current =
    members && members[index] ? members[index].relationship : "";
  if (index === 0) {
    return INSURANCE_RELATIONSHIP_OPTIONS.filter(function (opt) {
      return opt.value === "Self";
    });
  }
  var used = {};
  (members || []).forEach(function (row, i) {
    if (i === index) {
      return;
    }
    if (row && row.relationship) {
      used[row.relationship] = true;
    }
  });
  return [{ value: "", label: "Select Relationship" }].concat(
    ADDABLE_RELATIONSHIPS.filter(function (opt) {
      if (current && opt.value === current) {
        return true;
      }
      return !used[opt.value];
    })
  );
}

function canAddMoreMembers(members) {
  var used = {};
  (members || []).forEach(function (row) {
    if (row && row.relationship) {
      used[row.relationship] = true;
    }
  });
  return ADDABLE_RELATIONSHIPS.some(function (opt) {
    return !used[opt.value];
  });
}

function memberSchema() {
  return Yup.object({
    name: ruleSchema("personName", {
      label: "Full name",
      required: true,
    }),
    relationship: Yup.string()
      .trim()
      .required("Relationship is required")
      .test("not-empty", "Relationship is required", function (val) {
        return Boolean(val);
      }),
    dob: Yup.mixed()
      .nullable()
      .required("Date of birth is required")
      .test("not-future", "Future dates are not allowed", function (val) {
        if (!val) {
          return true;
        }
        var d = new Date(val);
        if (isNaN(d.getTime())) {
          return false;
        }
        var today = new Date();
        today.setHours(23, 59, 59, 999);
        return d.getTime() <= today.getTime();
      }),
  });
}

function buildValidationSchema() {
  return Yup.object({
    medicalInsurance: Yup.array()
      .of(memberSchema())
      .min(1, "At least Self member is required")
      .test(
        "unique-relationships",
        "Each relationship can only be added once",
        function (rows) {
          if (!rows || !rows.length) {
            return true;
          }
          var seen = {};
          for (var i = 0; i < rows.length; i++) {
            var rel = rows[i] && rows[i].relationship;
            if (!rel) {
              continue;
            }
            if (seen[rel]) {
              return this.createError({
                path: "medicalInsurance[" + i + "].relationship",
                message: rel + " is already added",
              });
            }
            seen[rel] = true;
          }
          return true;
        }
      ),
    insuranceLegalConsent: Yup.boolean()
      .oneOf([true], "Please acknowledge the details added by you are true")
      .required("Please acknowledge the details added by you are true"),
  });
}

function setBusy(onBusyChange, value) {
  if (typeof onBusyChange === "function") {
    onBusyChange(value);
  }
}

function scrollToFirstInsuranceError() {
  setTimeout(function () {
    var target = document.querySelector(
      '.Mui-error, [aria-invalid="true"], [data-ack-error="true"]'
    );
    if (target && typeof target.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, 50);
}

function InsuranceDetails(props) {
  var formikRef = props.formikRef;
  var changeTab = props.changeTab;
  var handleButtonClick = props.handleButtonClick;
  var onBusyChange = props.onBusyChange;
  var hideInternalActions = Boolean(props.hideInternalActions);
  var readOnly = Boolean(props.readOnly);

  var reduxUser = useSelector(selectUser);
  var params = useParams();
  var navigate = useNavigate();
  var id = params.id ? params.id : "";
  var user = reduxUser && reduxUser.user ? reduxUser.user : {};
  var isAdmin = Boolean(user.isAdmin);
  var userId = isAdmin ? id || props.id : user._id;

  var draftApi = useDraft("newJoinee", DRAFT_SECTION);
  var loadDraft = draftApi.loadDraft;
  var saveDraft = draftApi.saveDraft;
  var deleteDraft = draftApi.deleteDraft;

  var [initialValues, setInitialValues] = useState({
    medicalInsurance: [emptyInsuranceMember(userId, "Self")],
    insuranceLegalConsent: false,
    insuranceLegalConsentAt: null,
  });
  var [formKey, setFormKey] = useState(0);
  var [loading, setLoading] = useState(true);
  var formikInstanceRef = useRef(null);
  var deletedIdsRef = useRef([]);
  var policyYear = getInsurancePolicyYearLabel();

  useEffect(
    function () {
      var isActive = true;

      function finish(rows, profile, consent) {
        if (!isActive) {
          return;
        }
        var accepted = consent
          ? Boolean(consent.insuranceLegalConsent)
          : Boolean(profile && profile.insuranceLegalConsent);
        setInitialValues({
          medicalInsurance: hydrateSelfRow(rows, profile, userId),
          insuranceLegalConsent: accepted,
          insuranceLegalConsentAt: consent
            ? consent.insuranceLegalConsentAt || null
            : (profile && profile.insuranceLegalConsentAt) || null,
        });
        setFormKey(function (k) {
          return k + 1;
        });
        setLoading(false);
      }

      setLoading(true);
      deletedIdsRef.current = [];

      function loadApi(draftRows, draftConsent) {
        MedicalAPI.getMedicalInsurance(userId)
          .then(function (res) {
            if (!isActive) {
              return;
            }
            var profile = res && res.userData ? res.userData : null;
            var apiRows =
              res && res.data && res.data.length
                ? res.data
                : [
                    {
                      userId: userId,
                      name: profile && profile.name ? profile.name : "",
                      relationship: "Self",
                      dob:
                        profile && profile.dateOfBirth
                          ? profile.dateOfBirth
                          : null,
                    },
                  ];
            var rows =
              draftRows && draftRows.length ? draftRows : apiRows;
            finish(rows, profile, draftConsent);
          })
          .catch(function (err) {
            console.log(err);
            if (isActive) {
              setLoading(false);
              toast.error("Unable to load insurance details");
            }
          });
      }

      if (!isAdmin) {
        loadDraft()
          .then(function (loaded) {
            if (!isActive) {
              return;
            }
            var draftData = loaded && loaded.data ? loaded.data : loaded;
            var applied = applySectionDraftData(
              "newJoinee",
              DRAFT_SECTION,
              { medicalInsurance: [] },
              draftData
            );
            var draftRows =
              applied &&
              Array.isArray(applied.medicalInsurance) &&
              applied.medicalInsurance.length
                ? applied.medicalInsurance
                : null;
            var draftConsent =
              applied &&
              (typeof applied.insuranceLegalConsent !== "undefined" ||
                applied.insuranceLegalConsentAt)
                ? {
                    insuranceLegalConsent: Boolean(
                      applied.insuranceLegalConsent
                    ),
                    insuranceLegalConsentAt:
                      applied.insuranceLegalConsentAt || null,
                  }
                : null;
            loadApi(draftRows, draftConsent);
          })
          .catch(function () {
            loadApi(null, null);
          });
      } else {
        loadApi(null, null);
      }

      return function () {
        isActive = false;
      };
    },
    [userId, isAdmin, loadDraft]
  );

  function normalizeRows(rows) {
    return (rows || []).map(function (row, index) {
      return {
        _id: row._id,
        userId: userId,
        name: toTitleCaseName(row.name),
        relationship: index === 0 ? "Self" : row.relationship || "",
        dob: row.dob || null,
      };
    });
  }

  function consentPayload(values) {
    var accepted = Boolean(values.insuranceLegalConsent);
    return {
      insuranceLegalConsent: accepted,
      insuranceLegalConsentAt: accepted
        ? values.insuranceLegalConsentAt || new Date().toISOString()
        : null,
    };
  }

  function handleSaveDraft(formik) {
    if (isAdmin) {
      return Promise.resolve();
    }
    var rows = normalizeRows(formik.values.medicalInsurance);
    if (!rows[0] || !rows[0].dob) {
      toast.error(
        "Please complete Candidate Info date of birth before saving draft"
      );
      return Promise.resolve();
    }
    var consent = consentPayload(formik.values);
    var wrapped = pickSectionDraftData("newJoinee", DRAFT_SECTION, {
      medicalInsurance: rows,
      insuranceLegalConsent: consent.insuranceLegalConsent,
      insuranceLegalConsentAt: consent.insuranceLegalConsentAt,
    });
    setBusy(onBusyChange, "draft");
    return saveDraft(wrapped)
      .then(function (saved) {
        setBusy(onBusyChange, false);
        if (saved && saved.skipped) {
          toast.info("Nothing to save as draft");
          return saved;
        }
        if (saved) {
          if (
            consent.insuranceLegalConsent &&
            !formik.values.insuranceLegalConsentAt
          ) {
            formik.setFieldValue(
              "insuranceLegalConsentAt",
              consent.insuranceLegalConsentAt
            );
          }
          toast.success("Draft saved");
          return saved;
        }
        toast.error("Unable to save draft");
        return null;
      })
      .catch(function () {
        setBusy(onBusyChange, false);
        toast.error("Unable to save draft");
        return null;
      });
  }

  function handleSaveAndNext(values, formikHelpers) {
    var rows = normalizeRows(values.medicalInsurance);
    var consent = consentPayload(values);
    setBusy(onBusyChange, "next");
    if (typeof handleButtonClick === "function") {
      handleButtonClick("sectionE");
    }

    var deletes = (deletedIdsRef.current || []).map(function (delId) {
      return MedicalAPI.deleteMedicalInsurance(delId).catch(function () {
        return null;
      });
    });

    Promise.all(deletes)
      .then(function () {
        return Promise.all(
          rows.map(function (row) {
            var payload = Object.assign({}, row, consent);
            if (payload._id) {
              return MedicalAPI.updateMedicalInsurance(payload._id, payload);
            }
            return MedicalAPI.createMedicalInsurance(payload);
          })
        );
      })
      .then(function () {
        deletedIdsRef.current = [];
        var after = isAdmin ? Promise.resolve(true) : deleteDraft();
        return after.then(function () {
          if (isAdmin) {
            setBusy(onBusyChange, false);
            if (formikHelpers && formikHelpers.setSubmitting) {
              formikHelpers.setSubmitting(false);
            }
            toast.success("Insurance details saved");
            return null;
          }
          // Last New Joinee step → mark form submitted for Home status
          return UserAPI.newJoineeStatus(userId).then(function () {
            setBusy(onBusyChange, false);
            if (formikHelpers && formikHelpers.setSubmitting) {
              formikHelpers.setSubmitting(false);
            }
            if (
              consent.insuranceLegalConsent &&
              !values.insuranceLegalConsentAt &&
              formikHelpers &&
              formikHelpers.setFieldValue
            ) {
              formikHelpers.setFieldValue(
                "insuranceLegalConsentAt",
                consent.insuranceLegalConsentAt
              );
            }
            toast.success("New Joinee Form completed");
            navigate("/");
          });
        });
      })
      .catch(function (err) {
        console.log(err);
        setBusy(onBusyChange, false);
        if (formikHelpers && formikHelpers.setSubmitting) {
          formikHelpers.setSubmitting(false);
        }
        toast.error("Unable to save insurance details");
      });
  }

  useImperativeHandle(
    formikRef,
    function () {
      return {
        submitDraft: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return handleSaveDraft(formik);
        },
        submitNext: function () {
          var formik = formikInstanceRef.current;
          if (!formik || readOnly) {
            return Promise.resolve();
          }
          return formik.validateForm().then(function (errors) {
            if (errors && Object.keys(errors).length) {
              notifyFormInvalid(errors);
              var touchedMembers = (
                formik.values.medicalInsurance || []
              ).map(function () {
                return {
                  name: true,
                  relationship: true,
                  dob: true,
                };
              });
              formik.setTouched(
                {
                  medicalInsurance: touchedMembers,
                  insuranceLegalConsent: true,
                },
                true
              );
              scrollToFirstInsuranceError();
              return;
            }
            return formik.submitForm();
          });
        },
        getFormik: function () {
          return formikInstanceRef.current;
        },
      };
    },
    [userId, isAdmin, onBusyChange, changeTab, handleButtonClick, navigate, readOnly]
  );

  if (loading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography
          sx={{
            fontFamily: JOINEE_FORM_FONT,
            fontSize: 14,
            fontWeight: 400,
            color: JOINEE_FORM_COLORS.placeholder,
          }}
        >
          Loading insurance details…
        </Typography>
      </Box>
    );
  }

  return (
    <Formik
      key={formKey}
      initialValues={initialValues}
      enableReinitialize
      validationSchema={buildValidationSchema()}
      onSubmit={handleSaveAndNext}
    >
      {function (formik) {
        formikInstanceRef.current = formik;
        var values = formik.values;
        var members = values.medicalInsurance || [];
        var allowAdd = canAddMoreMembers(members);

        return (
          <Form noValidate>
            <Box
              component="fieldset"
              disabled={readOnly}
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                pointerEvents: readOnly ? "none" : "auto",
              }}
            >
            <FieldArray name="medicalInsurance">
              {function (arrayHelpers) {
                return (
                  <Box>
                    <FormSectionTitle
                      action={
                        <FormAddButton
                          disabled={!allowAdd}
                          onClick={function () {
                            if (!allowAdd) {
                              return;
                            }
                            arrayHelpers.push(
                              emptyInsuranceMember(userId, "")
                            );
                          }}
                        >
                          + Add Family Member
                        </FormAddButton>
                      }
                    >
                      Medical Insurance Details
                    </FormSectionTitle>

                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        alignSelf: "stretch",
                        width: "100%",
                        minHeight: 164,
                        py: "10px",
                        px: { xs: "12px", sm: "16px" },
                        gap: "10px",
                        bgcolor: "#F1F2F4",
                        borderRadius: "4px",
                        boxSizing: "border-box",
                        mb: 3,
                      }}
                    >
                      <Box
                        component="ul"
                        sx={{
                          m: 0,
                          width: "100%",
                          pl: "20px",
                          fontFamily: JOINEE_FORM_FONT,
                          fontSize: 14,
                          fontWeight: 400,
                          color: "#2E3338",
                          lineHeight: "22px",
                          "& li": { mb: "6px" },
                          "& li:last-child": { mb: 0 },
                        }}
                      >
                        <li>
                          General Policy coverage: Member + Spouse + 2 kids
                        </li>
                        <li>
                          Addition of parents to GMC: Member has to be bear a
                          nominal Insurance premium of Rs. 6000/- (Per head) for
                          the current year ({policyYear} in every year) to get
                          added under GMC, the same would be deducted in 2
                          instalments
                        </li>
                        <li>
                          Policy Sum Assured: Graded sum insured Rs. 3,00,000/-
                          (Unmarried) &amp; Rs. 4,00,000/-(Family)
                        </li>
                      </Box>
                    </Box>

                    {members.map(function (member, index) {
                      var base = "medicalInsurance[" + index + "]";
                      var isSelf = index === 0;
                      var options = relationshipOptionsForRow(members, index);
                      var rowKey =
                        (member && member._id) ||
                        "ins-" + index + "-" + (member.relationship || "new");

                      return (
                        <Box key={rowKey} sx={{ mb: 3.5 }}>
                          {!isSelf ? (
                            <FormSectionTitle
                              action={
                                <Button
                                  type="button"
                                  variant="outlined"
                                  onClick={function () {
                                    var row = members[index];
                                    if (row && row._id) {
                                      deletedIdsRef.current =
                                        deletedIdsRef.current.concat([
                                          row._id,
                                        ]);
                                    }
                                    arrayHelpers.remove(index);
                                  }}
                                  sx={{
                                    fontFamily: JOINEE_FORM_FONT,
                                    fontSize: 14,
                                    fontWeight: 400,
                                    textTransform: "none",
                                    color: JOINEE_FORM_COLORS.error,
                                    borderColor: JOINEE_FORM_COLORS.border,
                                    borderRadius: "6px",
                                    minHeight: 32,
                                  }}
                                >
                                  Remove
                                </Button>
                              }
                            >
                              Family Member {index}
                            </FormSectionTitle>
                          ) : null}

                          <FormTwoCol>
                            <FormTextField
                              id={"ins-name-" + index}
                              rule="personName"
                              name={base + ".name"}
                              label="Full Name"
                              placeholder="Enter Full Name"
                              required
                              value={member.name}
                              onChange={function (e) {
                                var raw = e.target.value;
                                if (raw === "" || NAME_REGEX.test(raw)) {
                                  formik.setFieldValue(base + ".name", raw);
                                }
                              }}
                              onBlur={function (e) {
                                formik.setFieldValue(
                                  base + ".name",
                                  toTitleCaseName(e.target.value)
                                );
                                formik.handleBlur(e);
                              }}
                              error={errMsg(formik, base + ".name")}
                              disabled={isSelf}
                              inputProps={{
                                maxLength: 50,
                                autoComplete: "off",
                              }}
                            />
                            <FormSelectField
                              id={"ins-rel-" + index}
                              name={base + ".relationship"}
                              label="Relationship"
                              placeholder="Select Relationship"
                              required
                              options={options}
                              value={member.relationship}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".relationship")}
                              disabled={isSelf}
                            />
                          </FormTwoCol>

                          <FormTwoCol>
                            <FormDateField
                              id={"ins-dob-" + index}
                              name={base + ".dob"}
                              label="Date of Birth"
                              placeholder="Select Date of Birth"
                              required
                              disableFuture
                              value={member.dob}
                              onChange={function (iso) {
                                formik.setFieldValue(base + ".dob", iso);
                              }}
                              onBlur={formik.handleBlur}
                              error={errMsg(formik, base + ".dob")}
                              disabled={isSelf}
                            />
                            <Box />
                          </FormTwoCol>
                        </Box>
                      );
                    })}
                  </Box>
                );
              }}
            </FieldArray>

            {!isAdmin ? (
              <Box
                sx={{
                  mt: 1,
                  mb: 1,
                  pt: 2,
                  borderTop: "1px solid " + JOINEE_FORM_COLORS.border,
                }}
                data-ack-error={
                  errMsg(formik, "insuranceLegalConsent") ? "true" : undefined
                }
              >
                <FormCheckboxField
                  name="insuranceLegalConsent"
                  checked={Boolean(values.insuranceLegalConsent)}
                  onChange={function (e) {
                    var checked = Boolean(e.target.checked);
                    formik.setFieldValue("insuranceLegalConsent", checked, true);
                    formik.setFieldValue(
                      "insuranceLegalConsentAt",
                      checked
                        ? values.insuranceLegalConsentAt ||
                            new Date().toISOString()
                        : null
                    );
                    formik.setFieldTouched("insuranceLegalConsent", true, false);
                    if (checked) {
                      formik.setFieldError(
                        "insuranceLegalConsent",
                        undefined
                      );
                    }
                  }}
                  label="I acknowledge the details added by me are true"
                />
                {errMsg(formik, "insuranceLegalConsent") &&
                !values.insuranceLegalConsent ? (
                  <Typography
                    sx={{
                      fontFamily: JOINEE_FORM_FONT,
                      fontSize: 12,
                      color: JOINEE_FORM_COLORS.error,
                      mt: 0.5,
                      ml: "28px",
                    }}
                  >
                    {errMsg(formik, "insuranceLegalConsent")}
                  </Typography>
                ) : null}
              </Box>
            ) : null}

            </Box>

            {!hideInternalActions ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1.5,
                  mt: 2,
                }}
              >
                <Button
                  type="button"
                  variant="outlined"
                  onClick={function () {
                    handleSaveDraft(formik);
                  }}
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                  }}
                >
                  Save as Draft
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    fontFamily: JOINEE_FORM_FONT,
                    textTransform: "none",
                    fontWeight: 400,
                    bgcolor: JOINEE_FORM_COLORS.primary,
                  }}
                >
                  Save
                </Button>
              </Box>
            ) : null}
          </Form>
        );
      }}
    </Formik>
  );
}

export default InsuranceDetails;
